import React, { createContext, useState, useCallback, useEffect } from 'react';
import authApi from '../api/auth';
import analyticsTracker from '../utils/analyticsTracker';

// Створюємо контекст
export const AuthContext = createContext(null);

// Провайдер контексту
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Функція для витягування імені з email
   */
  const extractNameFromEmail = (email) => {
    if (!email) return 'Користувач';
    
    const emailPart = email.split('@')[0];
    // Форматуємо email part (замінюємо крапки та підкреслення на пробіли)
    return emailPart
      .replace(/[._]/g, ' ')
      .split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  /**
   * Функція для очищення тексту від спеціальних символів
   */
  const cleanText = (text) => {
    if (!text) return '';

    // Видаляємо тільки контрольні символи та зайві пробіли
    // Залишаємо всі літери, цифри та базові розділові знаки
    return text
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Видаляємо тільки контрольні символи
      .replace(/\s+/g, ' ') // Замінюємо множинні пробіли на один
      .trim();
  };

  /**
   * Функція для очищення даних користувача
   */
  const cleanUserData = (userData) => {
    if (!userData) return null;
    
    const cleanedFullName = cleanText(userData.fullName);
    
    return {
      ...userData,
      fullName: cleanedFullName || extractNameFromEmail(userData.email) || 'Користувач'
    };
  };

  /**
   * Перевірка статусу авторизації
   */
  const checkAuth = useCallback(async () => {
    setError(null);

    const token = localStorage.getItem('token');

    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    // Миттєве відновлення з localStorage — без очікування мережі
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      try {
        setUser(cleanUserData(JSON.parse(cachedUser)));
      } catch {}
    }
    setLoading(false);

    // Фонова валідація токена
    try {
      const result = await authApi.validateToken();

      if (result.valid && result.user) {
        const cleanedUser = cleanUserData(result.user);
        setUser(cleanedUser);
        localStorage.setItem('user', JSON.stringify(cleanedUser));

        if (cleanedUser.id) {
          analyticsTracker.linkToUser(cleanedUser.id);
        }
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  }, []);

  /**
   * Вхід через Google
   */
  const loginWithGoogle = async (authData) => {
    console.log('🔐 Starting Google login...', authData);
    setLoading(true);
    setError(null);

    try {
      if (!authData || (!authData.code && !authData.idToken)) {
        throw new Error('Не отримано даних авторизації від Google');
      }

      console.log('📤 Sending auth data to backend');

      // Викликаємо API з переданими даними (може бути code або idToken)
      const response = await authApi.loginWithGoogle(authData);

      console.log('📥 API Response:', response);

      if (response.success) {
        const cleanedUser = cleanUserData(response.user);
        setUser(cleanedUser);

        console.log('✅ Login successful, user set:', cleanedUser);

        // Прив'язуємо поточну сесію до користувача для аналітики
        if (cleanedUser.id) {
          analyticsTracker.linkToUser(cleanedUser.id);
        }

        return {
          success: true,
          user: cleanedUser
        };
      } else {
        // Авторизація не успішна
        console.warn('⚠️ Login not successful:', response.error);

        return {
          success: false,
          error: response.error
        };
      }
    } catch (error) {
      console.error('❌ Login error:', error);

      let errorMessage = 'Помилка авторизації';

      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setError(errorMessage);

      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };


  /**
   * Вихід з системи
   */
  const logout = useCallback(() => {
    console.log('👋 Logging out...');
    
    // Очищаємо стан
    setUser(null);
    setError(null);
    
    // Викликаємо API logout
    authApi.logout();
  }, []);

  /**
   * Перевірка ролей користувача
   */
  const hasRole = useCallback((role) => {
    if (!user || !user.roles) return false;
    
    // Перевіряємо різні формати ролей
    return user.roles.some(r => 
      r === role || 
      r === `ROLE_${role}` || 
      r?.name === role || 
      r?.name === `ROLE_${role}`
    );
  }, [user]);

  /**
   * Перевірка чи є користувач адміністратором
   */
  const isAdmin = useCallback(() => {
    return hasRole('ADMIN');
  }, [hasRole]);

  /**
   * Перевірка чи авторизований користувач
   */
  const isAuthenticated = Boolean(user && localStorage.getItem('token'));

  /**
   * При завантаженні компонента перевіряємо авторизацію
   */
  useEffect(() => {
    console.log('🚀 AuthContext mounted, checking initial auth...');
    checkAuth();
  }, [checkAuth]);

  /**
   * Логування змін стану для дебагу
   */
  useEffect(() => {
    console.log('👤 User state changed:', {
      user: user,
      isAuthenticated: isAuthenticated,
      isAdmin: isAdmin()
    });
  }, [user, isAuthenticated, isAdmin]);

  // Значення контексту
  const contextValue = {
    // Стан
    user,
    loading,
    error,
    isAuthenticated,
    
    // Методи
    loginWithGoogle,
    logout,
    checkAuth,
    
    // Перевірка ролей
    hasRole,
    isAdmin: isAdmin(),
    
    // Допоміжні методи
    setError
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;